import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import RegisterModal from "../components/RegisterModal";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import PublicFooter from "../components/PublicFooter";
import { login as apiLogin, auth } from "../lib/api";
import { professionalAuth } from "../lib/professionalApi";
import { plansPublicApi } from "../lib/plansApi";
import PlanCard from "../components/PlanCard";
import useSeoMeta from "../hooks/useSeoMeta";

// Rodando dentro do app nativo (Capacitor injeta window.Capacitor mesmo
// carregando o site remoto via server.url).
const isNativeApp = !!(
    typeof window !== "undefined" &&
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
);

/**
 * Module A — Landing Page + Multi-tenant Login.
 * Faz autenticação real via POST /api/auth/login. Se a API falhar,
 * cai em modo protótipo (mesma UX de antes) para não travar o desenvolvimento.
 */
const LandingLogin = () => {
    useSeoMeta();
    const { brand, updateBrand, setLoggedIn, setActiveModule, syncTenantData } = useSalon();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [registerOpen, setRegisterOpen] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email.trim() || !email.includes("@")) {
            toast.error("Informe um e-mail válido.");
            return;
        }
        setLoading(true);
        try {
            const res = await apiLogin(email.trim(), password);

            // Login unificado: se o backend identificou um COLABORADOR (login
            // pessoal do profissional), a sessão é outra — token próprio
            // (kinng.professional.token), shell própria (ProfessionalShell).
            // Nunca cai no fluxo do CRM do dono.
            if (res.role === "professional") {
                auth.clear();                       // remove o token de tenant que o apiLogin setou
                professionalAuth.setToken(res.token);
                toast.success(`Olá, ${(res.user?.name || "").split(" ")[0] || "colega"}!`);
                window.location.href = "/";         // App remonta → ProfessionalShell
                return;
            }

            const t = res.tenant || {};
            updateBrand({
                shopId: t.shop_id || brand.shopId,
                shopName: t.shop_name || brand.shopName,
                plan: (t.plan || brand.plan || "royal").toLowerCase() === "imperial" ? "Imperial"
                    : (t.plan || "").toLowerCase() === "royal" ? "Royal"
                    : (t.plan || "").toLowerCase() === "classic" ? "Classic"
                    : brand.plan,
            });
            setLoggedIn(true);
            setActiveModule("home");
            // Baixa clientes/pros/services/appointments do backend (Fase 2)
            if (typeof syncTenantData === "function") { syncTenantData(); }
            toast.success("Bem-vindo de volta.", { description: "Acesso concedido via API." });
        } catch (err) {
            // Falha no login → SEMPRE limpar qualquer token remanescente e permanecer na tela de login.
            // Sem fallback offline silencioso (evita login-loop com dados protegidos).
            auth.clear();
            const status = err?.status;
            const isNetworkError = !status || (err?.message || "").toLowerCase().includes("failed to fetch");
            let description;
            if (status && status >= 500) {
                description = "Servidor indisponível. Tente em alguns instantes.";
            } else if (isNetworkError) {
                description = "Servidor indisponível. Verifique sua conexão e tente novamente.";
            } else {
                description = err?.message || "E-mail ou senha inválidos.";
            }
            toast.error("Falha ao entrar", { description });
        } finally {
            setLoading(false);
        }
    };

    const openRegister = () => { window.location.href = "/register"; };

    // Card de login — reutilizado no layout web (coluna direita) e no layout
    // enxuto do app. Fecha sobre email/password/handleLogin do escopo.
    const loginCard = (
        <div className="relative">
            <div
                className="absolute -inset-px rounded-3xl opacity-60 blur-2xl"
                style={{ background: "linear-gradient(135deg, #d97706, #7c2d12)" }}
            ></div>
            <form
                onSubmit={handleLogin}
                data-testid="login-form"
                className="relative rounded-3xl border border-white/10 bg-slate-900/80 backdrop-blur-xl p-7 md:p-9 shadow-2xl"
            >
                <div className="flex items-center justify-between mb-7">
                    <div className="salon-heading text-2xl">Entrar no seu salão</div>
                    <div className="h-10 w-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
                        <i className="fa-solid fa-key text-amber-400"></i>
                    </div>
                </div>

                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400">E-mail do Dono</span>
                    <input
                        data-testid="login-email"
                        type="email"
                        autoComplete="off"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none focus:border-amber-500/60 transition-colors min-h-[48px]"
                    />
                </label>

                <label className="block mt-4">
                    <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400">Senha</span>
                    <div className="relative mt-2">
                        <input
                            data-testid="login-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full rounded-xl border border-white/10 bg-slate-950/60 pl-4 pr-11 py-3 text-white outline-none focus:border-amber-500/60 transition-colors min-h-[48px]"
                        />
                        <button
                            type="button"
                            data-testid="login-password-toggle"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                            className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200 transition-colors"
                        >
                            <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                        </button>
                    </div>
                </label>

                <div className="mt-3 text-right">
                    <button
                        type="button"
                        data-testid="link-forgot-password"
                        onClick={() => setForgotOpen(true)}
                        className="text-xs text-slate-400 hover:text-amber-300 transition-colors"
                    >
                        Esqueci minha senha
                    </button>
                </div>

                <button
                    type="submit"
                    data-testid="login-submit"
                    disabled={loading}
                    className="mt-5 w-full min-h-[52px] rounded-xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-slate-950 font-semibold uppercase tracking-[0.2em] text-sm transition-all duration-300 shadow-lg shadow-amber-500/20 disabled:opacity-60"
                >
                    {loading ? (
                        <>
                            <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                            Carregando tenant…
                        </>
                    ) : (
                        <>
                            Entrar no Painel <i className="fa-solid fa-arrow-right ml-2"></i>
                        </>
                    )}
                </button>

                <div className="mt-5 flex items-center text-xs text-slate-500">
                    <button
                        type="button"
                        data-testid="link-open-register"
                        onClick={openRegister}
                        className="text-amber-400/90 hover:text-amber-300 transition-colors"
                    >
                        Não tem conta? Cadastre-se grátis →
                    </button>
                </div>
            </form>
        </div>
    );

    // ─── Layout ENXUTO no app nativo: só logo + login (sem hero e sem pricing) ──
    if (isNativeApp) {
        return (
            <div
                data-testid="landing-login-screen"
                className="min-h-screen w-full bg-slate-950 text-white relative overflow-hidden flex flex-col"
            >
                <div
                    className="absolute inset-0 opacity-50 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(at 20% 0%, rgba(217,119,6,0.18) 0px, transparent 50%), radial-gradient(at 80% 100%, rgba(139,92,246,0.15) 0px, transparent 50%)",
                    }}
                ></div>

                <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10">
                    <div className="flex flex-col items-center gap-3 mb-8">
                        <img
                            src="/brand/kinng-mark-white.png"
                            alt="Kinng.app"
                            className="h-16 w-16 object-contain opacity-95"
                        />
                        <div className="salon-heading text-2xl">Kinng</div>
                    </div>
                    <div className="w-full max-w-sm">{loginCard}</div>
                </div>

                <ForgotPasswordModal
                    open={forgotOpen}
                    initialEmail={email}
                    onClose={() => setForgotOpen(false)}
                    onResetDone={(em) => setEmail(em)}
                />
            </div>
        );
    }

    return (
        <div
            data-testid="landing-login-screen"
            className="min-h-screen w-full bg-slate-950 text-white relative overflow-hidden"
        >
            {/* Ambient gradient */}
            <div
                className="absolute inset-0 opacity-50 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(at 20% 0%, rgba(217,119,6,0.18) 0px, transparent 50%), radial-gradient(at 80% 100%, rgba(139,92,246,0.15) 0px, transparent 50%)",
                }}
            ></div>

            {/* Top bar */}
            <header className="relative z-10 px-6 lg:px-12 pt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="h-12 w-12 flex items-center justify-center shrink-0">
                        <img src="/brand/kinng-mark-transparent.png" alt="Kinng.app" className="h-full w-full object-contain" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                            Plataforma SaaS
                        </div>
                        <div className="salon-heading text-lg leading-none">Kinng</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center gap-2 text-xs text-slate-400">
                        <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        4.300+ salões ativos
                    </div>
                    <button
                        data-testid="cta-register-btn"
                        onClick={() => { window.location.href = "/register"; }}
                        className="min-h-[44px] px-4 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-amber-500/20"
                    >
                        <i className="fa-solid fa-rocket mr-2"></i>Criar conta grátis
                    </button>
                    <a
                        href="/admin"
                        data-testid="cta-admin-link"
                        title="Painel mestre da plataforma"
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full border border-white/10 hover:border-indigo-500/60 text-slate-400 hover:text-indigo-400 transition-all"
                    >
                        <i className="fa-solid fa-shield-halved"></i>
                    </a>
                </div>
            </header>

            <main className="relative z-10 px-6 lg:px-12 pt-12 lg:pt-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start max-w-7xl mx-auto">
                {/* Left: value prop */}
                <section className="space-y-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 text-xs uppercase tracking-[0.25em] text-amber-300">
                        <i className="fa-solid fa-fire"></i> Dor real do salão
                    </div>
                    <h1 className="salon-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Reduza <span className="text-amber-400">no-show em 80%</span>,
                        <br />
                        feche caixa em <span className="text-amber-400">3 toques</span>.
                    </h1>
                    <p className="text-slate-300/90 text-base md:text-lg max-w-xl leading-relaxed">
                        Agenda inteligente com confirmação automática via WhatsApp, PDV touch,
                        comissões em tempo real e um site exclusivo para o seu salão — tudo em uma
                        única plataforma multi-tenant.
                    </p>

                    <div className="grid grid-cols-3 gap-4 max-w-xl">
                        {[
                            { k: "no-show", v: "-80%", icon: "fa-bell-slash" },
                            { k: "tempo caixa", v: "3 toques", icon: "fa-stopwatch" },
                            { k: "MRR plataforma", v: "R$ 1M", icon: "fa-coins" },
                        ].map((m) => (
                            <div
                                key={m.k}
                                className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 backdrop-blur"
                            >
                                <i className={`fa-solid ${m.icon} text-amber-400 mb-2`}></i>
                                <div className="salon-heading text-2xl">{m.v}</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                                    {m.k}
                                </div>
                            </div>
                        ))}
                    </div>

                    <ul className="space-y-3 max-w-xl">
                        {[
                            "Agenda cheia com lembretes WhatsApp automáticos",
                            "Domínio próprio + SSL ativado em 60 segundos",
                            "App do barbeiro lança consumo direto na cadeira",
                            "Painel financeiro bloqueado por PIN do dono",
                            "Modo Kiosk com saída travada por PIN — exclusivo Premium",
                        ].map((line) => (
                            <li key={line} className="flex items-start gap-3 text-slate-300">
                                <span className="mt-1 h-5 w-5 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                                    <i className="fa-solid fa-check text-amber-400 text-[10px]"></i>
                                </span>
                                <span className="text-sm md:text-base">{line}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Right: login card */}
                <section className="lg:sticky lg:top-6">
                    {loginCard}
                </section>
            </main>

            {/* ─── Plans Section ──────────────────────────────────────────────── */}
            <PricingSection setRegisterOpen={setRegisterOpen} />

            <div className="relative z-10 mt-20">
                <PublicFooter />
            </div>

            <RegisterModal open={registerOpen} onClose={() => setRegisterOpen(false)} />
            <ForgotPasswordModal
                open={forgotOpen}
                initialEmail={email}
                onClose={() => setForgotOpen(false)}
                onResetDone={(em) => setEmail(em)}
            />
        </div>
    );
};

import { FEATURE_LABELS } from "../components/PlanCard";

// Grid responsivo por quantidade de planos
const LANDING_GRID_COLS = {
    1: "grid-cols-1 max-w-sm mx-auto",
    2: "grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto",
    3: "grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto",
};

const PricingSection = ({ setRegisterOpen }) => {
    const [cycle, setCycle] = useState("monthly");
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        plansPublicApi.list()
            .then((list) => setPlans((list || []).filter((p) => p.active !== false)))
            .catch(() => { /* silencioso */ })
            .finally(() => setLoading(false));
    }, []);

    const hasAnnual = plans.some((p) => p.price_annual && p.price_annual > 0);
    const gridCols = LANDING_GRID_COLS[plans.length] || "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto";
    const highlightIdx = plans.length > 1 ? Math.floor(plans.length / 2) : -1;

    return (
        <section
            id="planos"
            data-testid="landing-pricing-section"
            className="relative z-10 px-6 lg:px-12 py-20 mt-10"
        >
            {/* Heading */}
            <div className="max-w-3xl mx-auto text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-300 text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
                    <i className="fa-solid fa-crown"></i>
                    Escolha seu nível de gestão
                </div>
                <h2 className="salon-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
                    Planos feitos para
                    <br />
                    <span className="text-amber-400">salões</span> que querem liderar.
                </h2>
                <p className="text-slate-400 mt-5 text-sm sm:text-base max-w-2xl mx-auto">
                    Do essencial ao avançado. Escolha o plano ideal para o momento do seu salão
                    e escale sua operação com inteligência.
                </p>

                {hasAnnual && (
                    <div className="mt-8 inline-flex items-center gap-1 p-1.5 rounded-2xl border border-amber-500/25 bg-black/40 backdrop-blur">
                        <button
                            type="button"
                            data-testid="pricing-cycle-monthly"
                            onClick={() => setCycle("monthly")}
                            className={`px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all flex flex-col items-center min-h-[52px] justify-center min-w-[140px] ${
                                cycle === "monthly"
                                    ? "bg-amber-500/15 text-amber-200 border border-amber-500/40 shadow-[0_0_24px_-12px_rgba(212,175,55,0.6)]"
                                    : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            <span>Mensal</span>
                            <span className="text-[10px] font-normal text-slate-500 normal-case tracking-normal mt-0.5">Cancele quando quiser</span>
                        </button>
                        <button
                            type="button"
                            data-testid="pricing-cycle-yearly"
                            onClick={() => setCycle("yearly")}
                            className={`px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all flex flex-col items-center min-h-[52px] justify-center min-w-[140px] relative ${
                                cycle === "yearly"
                                    ? "bg-amber-500/15 text-amber-200 border border-amber-500/40 shadow-[0_0_24px_-12px_rgba(212,175,55,0.6)]"
                                    : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            <span className="flex items-center gap-1.5">
                                Anual
                                <span className="px-1.5 py-0.5 rounded-md bg-amber-500 text-slate-950 text-[9px] font-bold">Economize</span>
                            </span>
                            <span className="text-[10px] font-normal text-slate-500 normal-case tracking-normal mt-0.5">até 2 meses grátis</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Plan cards */}
            {loading && (
                <div className="text-center text-slate-500 text-sm py-10" data-testid="pricing-loading">
                    Carregando planos...
                </div>
            )}
            {!loading && plans.length === 0 && (
                <div className="text-center text-slate-500 text-sm py-10">
                    Nenhum plano disponível no momento.
                </div>
            )}
            <div className={`grid gap-5 ${gridCols}`} data-testid="pricing-plan-grid">
                {plans.map((plan, idx) => {
                    const displayPlan = cycle === "yearly" && plan.price_annual > 0
                        ? { ...plan, price_monthly: plan.price_annual / 12 }
                        : plan;
                    return (
                        <PlanCard
                            key={plan.slug || plan.id}
                            plan={displayPlan}
                            highlighted={idx === highlightIdx}
                            onSelect={() => setRegisterOpen(true)}
                            variant="landing"
                            testId={`pricing-plan-${plan.slug}`}
                            ctaLabel={`Escolher ${plan.name}`}
                        />
                    );
                })}
            </div>

            {/* Comparison table (dinâmica a partir dos features) */}
            {plans.length > 1 && <ComparisonTable plans={plans} />}

            {/* CTA card */}
            <div className="max-w-6xl mx-auto mt-12 rounded-2xl border border-amber-500/25 bg-black/55 backdrop-blur-xl p-8 grid md:grid-cols-3 gap-6 items-center" data-testid="pricing-cta-card">
                {/* Guarantee */}
                <div className="text-center md:text-left flex md:block items-center gap-4 md:gap-0">
                    <div className="h-16 w-16 rounded-full border-2 border-amber-500/50 bg-amber-500/10 text-amber-300 flex items-center justify-center text-2xl mb-0 md:mb-4 shadow-[0_0_24px_-8px_rgba(212,175,55,0.5)] flex-shrink-0">
                        <i className="fa-solid fa-shield-halved"></i>
                    </div>
                    <div>
                        <div className="text-amber-300 text-xs uppercase tracking-[0.25em] font-bold">7 dias de garantia</div>
                        <p className="text-slate-400 text-xs mt-2 leading-relaxed">
                            Teste o Kinng.app por 7 dias.<br />
                            Se não amar, é só cancelar.<br />
                            Sem burocracia.
                        </p>
                    </div>
                </div>
                {/* Main CTA */}
                <div className="text-center">
                    <h4 className="salon-heading text-xl sm:text-2xl text-white leading-tight">
                        Pronto para transformar<br />a gestão do seu salão?
                    </h4>
                    <p className="text-slate-400 text-xs mt-3 max-w-xs mx-auto">
                        Junte-se a centenas de donos que já tomam decisões mais inteligentes e lucram mais todos os dias.
                    </p>
                    <button
                        type="button"
                        data-testid="pricing-cta-start"
                        onClick={() => setRegisterOpen(true)}
                        className="mt-5 inline-flex items-center justify-center gap-2 px-6 min-h-[52px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs uppercase tracking-[0.2em] font-bold transition-all shadow-lg shadow-amber-500/30 hover:scale-[1.03]"
                    >
                        <i className="fa-solid fa-calendar-check"></i>
                        Começar agora
                    </button>
                    <p className="text-[10px] text-slate-500 mt-3">Não precisa de cartão de crédito</p>
                </div>
                {/* Social proof */}
                <div className="text-center md:text-right">
                    <div className="flex -space-x-2 justify-center md:justify-end mb-3">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <img
                                key={i}
                                src={`https://i.pravatar.cc/60?img=${i * 12}`}
                                alt=""
                                className="h-10 w-10 rounded-full border-2 border-amber-500/40 ring-2 ring-slate-950"
                            />
                        ))}
                    </div>
                    <div className="flex justify-center md:justify-end gap-0.5 text-amber-400 text-xs mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <i key={i} className="fa-solid fa-star"></i>
                        ))}
                    </div>
                    <p className="text-xs text-slate-400">
                        Mais de <span className="font-bold text-amber-300">1.000 salões</span> já confiam<br />no Kinng.app para crescer.
                    </p>
                </div>
            </div>

            {/* Trust strip */}
            <div className="max-w-6xl mx-auto mt-10 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="pricing-trust-strip">
                <TrustItem icon="fa-bolt" title="Implementação rápida" desc="Comece em minutos" />
                <TrustItem icon="fa-shield-halved" title="Dados 100% seguros" desc="Hospedagem criptografada" />
                <TrustItem icon="fa-arrows-rotate" title="Atualizações constantes" desc="Novas funções todo mês" />
                <TrustItem icon="fa-headset" title="Suporte humano" desc="Sempre que precisar" />
            </div>
        </section>
    );
};

const ComparisonTable = ({ plans }) => {
    // Só lista as features que aparecem em pelo menos um plano
    const activeFeatureKeys = Object.keys(FEATURE_LABELS).filter((k) =>
        plans.some((p) => p.features && p.features[k])
    );
    if (activeFeatureKeys.length === 0) return null;

    return (
        <div className="max-w-6xl mx-auto mt-20" data-testid="pricing-comparison">
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 text-amber-300/80 text-[10px] uppercase tracking-[0.3em] font-bold">
                    <span className="h-px w-8 bg-amber-500/30"></span>
                    Compare todos os recursos
                    <span className="h-px w-8 bg-amber-500/30"></span>
                </div>
                <h3 className="salon-heading text-3xl sm:text-4xl text-white mt-3">
                    Encontre o plano ideal para o seu momento.
                </h3>
            </div>
            <div className="rounded-2xl border border-amber-500/15 bg-black/45 backdrop-blur-xl overflow-x-auto">
                <table className="w-full min-w-[600px]">
                    <thead>
                        <tr className="border-b border-amber-500/20 bg-amber-500/5">
                            <th className="text-left px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Recursos</th>
                            {plans.map((p) => (
                                <th key={p.slug} className="text-center px-4 py-4 text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">
                                    {p.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {activeFeatureKeys.map((k, i) => (
                            <tr
                                key={k}
                                className={`text-sm border-b border-amber-500/8 last:border-0 ${i % 2 ? "bg-amber-500/[0.025]" : ""}`}
                            >
                                <td className="px-6 py-3 text-slate-300 flex items-center gap-2">
                                    <i className={`fa-solid ${FEATURE_LABELS[k].icon} text-amber-300/60 text-xs`}></i>
                                    {FEATURE_LABELS[k].label}
                                </td>
                                {plans.map((p) => (
                                    <td key={p.slug} className="px-4 py-3 text-center">
                                        {(p.features || {})[k]
                                            ? <i className="fa-solid fa-check text-amber-400"></i>
                                            : <span className="text-slate-600 text-base">—</span>}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const TrustItem = ({ icon, title, desc }) => (
    <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center flex-shrink-0">
            <i className={`fa-solid ${icon}`}></i>
        </div>
        <div className="min-w-0">
            <div className="text-xs font-semibold text-white truncate">{title}</div>
            <div className="text-[10px] text-slate-500 truncate">{desc}</div>
        </div>
    </div>
);


export default LandingLogin;
